"use package";

import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "@/hooks";
import { AppDispatch } from "@/store";
import { UseDataRangeFilter } from "@/types";
import { toast } from "react-toastify";
import {
  fetchFullCommission,
  fetchPaymentsHistoryCommmission,
} from "@/store/modules/fullCommission/thunks";
import { useAuth } from "@/context/AuthContext";

const usePaymentHistoryCommission = ({
  dateRange,
  onSuccess,
}: UseDataRangeFilter) => {
  const { params, setParams, handlePage, updateParams } = useParams();
  const { per_page, current_page } = params;
  const { currency, establishment } = useAuth();

  const { paymentHistoryCommission, loading, error } = useSelector(
    (state: any) => state.fullCommission,
  );
  const dispatch: AppDispatch = useDispatch();

  const payload = useMemo(
    () => ({
      ...params,
      start_date: dateRange?.start?.toISOString().split("T")[0],
      end_date: dateRange?.end?.toISOString().split("T")[0],
    }),
    [params, dateRange?.start, dateRange?.end],
  );

  // Busca inicial quando o estabelecimento ou o intervalo de datas estiverem disponíveis
  useEffect(() => {
    if (establishment?.id && dateRange?.start && dateRange?.end) {
      const initialPayload = {
        ...params,
        filter: {
          ...params.filter,
          establishment_id: establishment.id,
        },
        start_date: dateRange.start.toISOString().split("T")[0],
        end_date: dateRange.end.toISOString().split("T")[0],
      };
      dispatch(fetchPaymentsHistoryCommmission(initialPayload));
    }
  }, [establishment?.id, dateRange?.start, dateRange?.end, params, dispatch]); // Re-run when establishment or dateRange change

  const totalItems = paymentHistoryCommission?.total ?? 0;
  const itemsPerPage = paymentHistoryCommission?.per_page ?? 10;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / itemsPerPage) : 1;

  const [currentPage, setCurrentPage] = useState(
    paymentHistoryCommission?.current_page ?? 1,
  );

  useEffect(() => {
    const page = paymentHistoryCommission?.current_page;
    if (page && page !== currentPage) {
      setCurrentPage(page);
    }
  }, [paymentHistoryCommission?.current_page]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    handlePage(page);
  };

  const handleFilterChange = (value: string) => {
    updateParams({
      search: value,
      current_page: 1,
    });
    setCurrentPage(1);
  };

  return {
    paymentHistoryCommission,
    loading,
    error,
    currentPage,
    handlePageChange,
    totalPages,
    itemsPerPage,
    currency,
    handleFilterChange,
  };
};

export default usePaymentHistoryCommission;
